/**
 * Statistics Page
 *
 * Displays income and expense statistics with pie charts.
 * Features drill-down into subcategories and configurable category count.
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { useAccountStore } from '../store/accountStore';
import { useUserSettingsStore, getDefaultAccountId } from '../store/userSettingsStore';
import { statisticsService, CategoryStatsResponse } from '../services/statistics.service';
import { Card, Button } from '../components/ui';
import { formatCurrency } from '../utils/formatters';
import { STATISTICS_CATEGORIES_DEFAULT } from '@budgira/shared';

// Colors for the pie charts - distinct for each category
const COLORS = [
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#f59e0b', // amber-500
  '#10b981', // emerald-500
  '#ef4444', // red-500
  '#ec4899', // pink-500
  '#3b82f6', // blue-500
  '#f97316', // orange-500
  '#14b8a6', // teal-500
  '#a855f7', // purple-500
  '#84cc16', // lime-500
  '#6366f1', // indigo-500
  '#22c55e', // green-500
  '#e11d48', // rose-600
  '#0ea5e9', // sky-500
  '#eab308', // yellow-500
  '#7c3aed', // violet-600
  '#059669', // emerald-600
  '#dc2626', // red-600
  '#d946ef', // fuchsia-500
];

const OTHER_COLOR = '#94a3b8'; // slate-400

interface BreadcrumbItem {
  categoryId: string | null;
  categoryName: string;
}

interface PieChartData {
  name: string;
  value: number;
  percentage: number;
  categoryId: string | null;
  logo: string | null;
  isOther?: boolean;
  hasChildren?: boolean;
}

const StatisticsPage = () => {
  const { t, i18n } = useTranslation();
  const { accounts, fetchAccounts, isLoading: isLoadingAccounts } = useAccountStore();
  const { preferences } = useUserSettingsStore();

  // State
  const [selectedAccountId, setSelectedAccountId] = useState<string>('all');
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

  // Drill-down navigation for expenses
  const [expenseBreadcrumb, setExpenseBreadcrumb] = useState<BreadcrumbItem[]>([
    { categoryId: null, categoryName: t('statistics.allCategories', 'Toutes les catégories') },
  ]);
  const currentExpenseParentId = expenseBreadcrumb[expenseBreadcrumb.length - 1]?.categoryId ?? null;

  // Drill-down navigation for income
  const [incomeBreadcrumb, setIncomeBreadcrumb] = useState<BreadcrumbItem[]>([
    { categoryId: null, categoryName: t('statistics.allCategories', 'Toutes les catégories') },
  ]);
  const currentIncomeParentId = incomeBreadcrumb[incomeBreadcrumb.length - 1]?.categoryId ?? null;

  // Data states
  const [expenseStats, setExpenseStats] = useState<CategoryStatsResponse | null>(null);
  const [incomeStats, setIncomeStats] = useState<CategoryStatsResponse | null>(null);
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
  const [isLoadingIncome, setIsLoadingIncome] = useState(false);

  // Get limit from preferences
  const categoriesLimit = preferences?.statisticsCategoriesCount ?? STATISTICS_CATEGORIES_DEFAULT;

  // Calculate date range for the selected month
  const dateRange = useMemo(() => {
    const start = new Date(selectedYear, selectedMonth - 1, 1);
    const end = new Date(selectedYear, selectedMonth, 0); // Last day of month
    return {
      startDate: start.toISOString().split('T')[0] as string,
      endDate: end.toISOString().split('T')[0] as string,
    };
  }, [selectedYear, selectedMonth]);
  
  const startDate = dateRange.startDate;
  const endDate = dateRange.endDate;

  // Format selected month name
  const selectedMonthName = useMemo(() => {
    return new Date(selectedYear, selectedMonth - 1, 1).toLocaleDateString(i18n.language, {
      month: 'long',
      year: 'numeric',
    });
  }, [selectedYear, selectedMonth, i18n.language]);

  // Check if current month
  const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;

  // Load accounts
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Set default account
  useEffect(() => {
    if (!isLoadingAccounts && accounts.length > 0 && selectedAccountId === 'all') {
      const defaultAccountId = getDefaultAccountId();
      if (defaultAccountId && accounts.some(acc => acc.id === defaultAccountId)) {
        // Keep 'all' if user prefers aggregate view
      }
    }
  }, [isLoadingAccounts, accounts, preferences, selectedAccountId]);

  // Fetch expense statistics
  const fetchExpenseStats = useCallback(async () => {
    setIsLoadingExpenses(true);
    try {
      const response = await statisticsService.getCategoryStats({
        accountId: selectedAccountId === 'all' ? undefined : selectedAccountId,
        startDate,
        endDate,
        direction: 'debit',
        parentCategoryId: currentExpenseParentId ?? undefined,
        limit: 50, // Request more, we'll slice in frontend based on user preference
      });
      if (response.success && response.data) {
        setExpenseStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching expense stats:', error);
    } finally {
      setIsLoadingExpenses(false);
    }
  }, [selectedAccountId, startDate, endDate, currentExpenseParentId, categoriesLimit]);

  // Fetch income statistics
  const fetchIncomeStats = useCallback(async () => {
    setIsLoadingIncome(true);
    try {
      const response = await statisticsService.getCategoryStats({
        accountId: selectedAccountId === 'all' ? undefined : selectedAccountId,
        startDate,
        endDate,
        direction: 'credit',
        parentCategoryId: currentIncomeParentId ?? undefined,
        limit: 50, // Request more, we'll slice in frontend based on user preference
      });
      if (response.success && response.data) {
        setIncomeStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching income stats:', error);
    } finally {
      setIsLoadingIncome(false);
    }
  }, [selectedAccountId, startDate, endDate, currentIncomeParentId, categoriesLimit]);

  useEffect(() => {
    fetchExpenseStats();
  }, [fetchExpenseStats]);

  useEffect(() => {
    fetchIncomeStats();
  }, [fetchIncomeStats]);

  // Navigation handlers
  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
    // Reset drill-down when changing month
    setExpenseBreadcrumb([{ categoryId: null, categoryName: t('statistics.allCategories', 'Toutes les catégories') }]);
    setIncomeBreadcrumb([{ categoryId: null, categoryName: t('statistics.allCategories', 'Toutes les catégories') }]);
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
    // Reset drill-down
    setExpenseBreadcrumb([{ categoryId: null, categoryName: t('statistics.allCategories', 'Toutes les catégories') }]);
    setIncomeBreadcrumb([{ categoryId: null, categoryName: t('statistics.allCategories', 'Toutes les catégories') }]);
  };

  const goToCurrentMonth = () => {
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth() + 1);
    setExpenseBreadcrumb([{ categoryId: null, categoryName: t('statistics.allCategories', 'Toutes les catégories') }]);
    setIncomeBreadcrumb([{ categoryId: null, categoryName: t('statistics.allCategories', 'Toutes les catégories') }]);
  };

  // Drill-down handlers
  const handleExpenseCategoryClick = (data: PieChartData) => {
    if (data.isOther || !data.categoryId || !data.hasChildren) return;
    setExpenseBreadcrumb(prev => [...prev, { categoryId: data.categoryId, categoryName: data.name }]);
  };

  const handleIncomeCategoryClick = (data: PieChartData) => {
    if (data.isOther || !data.categoryId || !data.hasChildren) return;
    setIncomeBreadcrumb(prev => [...prev, { categoryId: data.categoryId, categoryName: data.name }]);
  };

  const handleExpenseBreadcrumbClick = (index: number) => {
    setExpenseBreadcrumb(prev => prev.slice(0, index + 1));
  };

  const handleIncomeBreadcrumbClick = (index: number) => {
    setIncomeBreadcrumb(prev => prev.slice(0, index + 1));
  };

  // Transform data for charts
  // Note: categoriesLimit includes "Others", so we show (limit-1) top categories + Others
  const displayLimit = Math.max(1, categoriesLimit - 1);
  
  const expenseChartData: PieChartData[] = useMemo(() => {
    if (!expenseStats) return [];
    const topCategories = expenseStats.categories.slice(0, displayLimit);
    const remainingCategories = expenseStats.categories.slice(displayLimit);
    
    const data: PieChartData[] = topCategories.map(cat => ({
      name: cat.categoryName,
      value: cat.totalAmount,
      percentage: cat.percentage,
      categoryId: cat.categoryId,
      logo: cat.categoryLogo,
      hasChildren: cat.hasChildren,
    }));
    
    // Calculate "Others" from remaining categories + original otherAmount
    const remainingAmount = remainingCategories.reduce((sum, cat) => sum + cat.totalAmount, 0) + expenseStats.otherAmount;
    const total = expenseStats.total;
    
    if (remainingAmount > 0) {
      data.push({
        name: t('statistics.other', 'Autres'),
        value: remainingAmount,
        percentage: total > 0 ? (remainingAmount / total) * 100 : 0,
        categoryId: null,
        logo: null,
        isOther: true,
      });
    }
    return data;
  }, [expenseStats, t, displayLimit]);

  const incomeChartData: PieChartData[] = useMemo(() => {
    if (!incomeStats) return [];
    const topCategories = incomeStats.categories.slice(0, displayLimit);
    const remainingCategories = incomeStats.categories.slice(displayLimit);
    
    const data: PieChartData[] = topCategories.map(cat => ({
      name: cat.categoryName,
      value: cat.totalAmount,
      percentage: cat.percentage,
      categoryId: cat.categoryId,
      logo: cat.categoryLogo,
      hasChildren: cat.hasChildren,
    }));
    
    // Calculate "Others" from remaining categories + original otherAmount
    const remainingAmount = remainingCategories.reduce((sum, cat) => sum + cat.totalAmount, 0) + incomeStats.otherAmount;
    const total = incomeStats.total;
    
    if (remainingAmount > 0) {
      data.push({
        name: t('statistics.other', 'Autres'),
        value: remainingAmount,
        percentage: total > 0 ? (remainingAmount / total) * 100 : 0,
        categoryId: null,
        logo: null,
        isOther: true,
      });
    }
    return data;
  }, [incomeStats, t, displayLimit]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: PieChartData }> }) => {
    if (active && payload && payload.length > 0 && payload[0]) {
      const data = payload[0].payload;
      const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
      const currency = selectedAccount?.currency || 'EUR';

      return (
        <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg">
          <p className="font-medium text-slate-900 dark:text-slate-100 mb-1">{data.name}</p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {formatCurrency(data.value, currency)} ({data.percentage.toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label for pie chart
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: {
    cx?: number;
    cy?: number;
    midAngle?: number;
    innerRadius?: number;
    outerRadius?: number;
    percent?: number;
  }) => {
    if (!cx || !cy || !midAngle || !innerRadius || !outerRadius || percent === undefined) return null;
    if (percent < 0.05) return null; // Don't show label for very small slices
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-xs font-medium"
        style={{ pointerEvents: 'none' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Render pie chart card
  const renderPieChartCard = (
    title: string,
    chartData: PieChartData[],
    isLoading: boolean,
    total: number,
    breadcrumb: BreadcrumbItem[],
    onBreadcrumbClick: (index: number) => void,
    onCategoryClick: (data: PieChartData) => void,
    colorScheme: 'expense' | 'income'
  ) => {
    const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
    const currency = selectedAccount?.currency || 'EUR';

    return (
      <Card>
        <div className="flex flex-col h-full">
          {/* Title */}
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            {title}
          </h3>

          {/* Breadcrumb */}
          {breadcrumb.length > 1 && (
            <nav className="flex items-center gap-1 text-sm mb-4 flex-wrap">
              {breadcrumb.map((item, index) => (
                <span key={index} className="flex items-center">
                  {index > 0 && (
                    <svg className="w-4 h-4 text-slate-400 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  <button
                    onClick={() => onBreadcrumbClick(index)}
                    className={`hover:text-violet-600 dark:hover:text-violet-400 transition-colors ${
                      index === breadcrumb.length - 1
                        ? 'font-medium text-slate-900 dark:text-slate-100'
                        : 'text-slate-500 dark:text-slate-400'
                    }`}
                  >
                    {item.categoryName}
                  </button>
                </span>
              ))}
            </nav>
          )}

          {/* Total */}
          <div className="mb-4">
            <span className="text-sm text-slate-500 dark:text-slate-400">{t('statistics.total', 'Total')}:</span>
            <span className={`ml-2 text-xl font-bold ${colorScheme === 'expense' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {formatCurrency(total, currency)}
            </span>
          </div>

          {/* Chart */}
          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400">
              {t('statistics.noData', 'Aucune donnée pour cette période')}
            </div>
          ) : (
            <div className="h-64 [&_svg]:outline-none [&_*]:outline-none [&_.recharts-wrapper]:focus:outline-none">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={90}
                    innerRadius={40}
                    dataKey="value"
                    onClick={(_, index) => {
                      const item = chartData[index];
                      if (item) onCategoryClick(item);
                    }}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.isOther ? OTHER_COLOR : COLORS[index % COLORS.length]}
                        className="hover:opacity-80 transition-opacity focus:outline-none"
                        style={{ cursor: entry.hasChildren ? 'pointer' : 'default' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Legend list */}
          {chartData.length > 0 && (
            <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
              {chartData.map((entry, index) => (
                <button
                  key={entry.categoryId || 'other'}
                  onClick={() => !entry.isOther && entry.hasChildren && onCategoryClick(entry)}
                  className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                    entry.isOther || !entry.hasChildren ? 'cursor-default' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: entry.isOther ? OTHER_COLOR : COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                      {entry.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {formatCurrency(entry.value, currency)}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      ({entry.percentage.toFixed(1)}%)
                    </span>
                    {!entry.isOther && (
                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {t('statistics.title', 'Statistiques')}
        </h1>
        <p className="text-slate-500 dark:text-slate-400">
          {t('statistics.subtitle', 'Analysez vos dépenses et revenus par catégorie')}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Month navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title={t('statistics.previousMonth', 'Mois précédent')}
            >
              <svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-lg font-medium text-slate-900 dark:text-slate-100 min-w-[160px] text-center capitalize">
              {selectedMonthName}
            </span>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title={t('statistics.nextMonth', 'Mois suivant')}
            >
              <svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {!isCurrentMonth && (
              <Button
                variant="ghost"
                size="sm"
                onClick={goToCurrentMonth}
              >
                {t('statistics.today', 'Aujourd\'hui')}
              </Button>
            )}
          </div>

          {/* Account selector */}
          <select
            value={selectedAccountId}
            onChange={(e) => {
              setSelectedAccountId(e.target.value);
              // Reset drill-down when changing account
              setExpenseBreadcrumb([{ categoryId: null, categoryName: t('statistics.allCategories', 'Toutes les catégories') }]);
              setIncomeBreadcrumb([{ categoryId: null, categoryName: t('statistics.allCategories', 'Toutes les catégories') }]);
            }}
            className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
          >
            <option value="all">{t('statistics.allAccounts', 'Tous les comptes')}</option>
            {accounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {/* Pie Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expenses */}
        {renderPieChartCard(
          t('statistics.expenses', 'Dépenses'),
          expenseChartData,
          isLoadingExpenses,
          expenseStats?.total ?? 0,
          expenseBreadcrumb,
          handleExpenseBreadcrumbClick,
          handleExpenseCategoryClick,
          'expense'
        )}

        {/* Income */}
        {renderPieChartCard(
          t('statistics.income', 'Revenus'),
          incomeChartData,
          isLoadingIncome,
          incomeStats?.total ?? 0,
          incomeBreadcrumb,
          handleIncomeBreadcrumbClick,
          handleIncomeCategoryClick,
          'income'
        )}
      </div>

      {/* Settings hint */}
      <div className="text-sm text-slate-500 dark:text-slate-400 text-center">
        {t('statistics.settingsHint', 'Le nombre de catégories affichées ({{count}}) peut être modifié dans les paramètres.', { count: categoriesLimit })}
      </div>
    </div>
  );
};

export default StatisticsPage;
